'use client';

// The front door: what you can do, per project.
//
// It used to answer "what is urgent", which is a different question and was the
// morning digest's question. That version drew three tiers — pressing, quiet,
// and a third for projects with no milestone — plus a calendar above them, and
// a project with a deadline and no milestone appeared *three times* on one
// phone screen under three headings.
//
// Worse, and the reason this changed rather than got tidier: every tier read
// `projects/*/project.md`, so the whole page was empty for anybody whose wiki
// was made this week. The front door of the app rendered "no projects yet" to
// the people it most needed to give a first move to.
//
// So: one row per project, ranked, with urgency said on the row rather than by
// which heading it sits under. The calendar strip stays, because when two
// things land in the same week is genuinely different information from what
// each of them is. And the empty state is a button, because an empty front
// door should be an invitation rather than a report.
//
// **One row per project, never per repo.** Lorekeeper's engine and its site are
// two different things with two different sets of verbs, and both of those
// belong on Lorekeeper's own page. The aim here is to minimise how much has to
// be read before you can act, not how many presses it takes to act — every repo
// listed with its verbs inline is a page you scroll rather than a page you take
// in.

import { useState } from 'react';
import { useConsole } from './console';
import { ranked, timeline, unscheduled, pressingDates, nagging, type BoardEntry } from '@/lib/board';
import { countdown, daysUntil, stamp } from '@/lib/project';
import { registerOf, words } from '@/lib/say';

export function Board() {
  const { projects, projectsError, me, openPath, createProject, onStreamScroll } = useConsole();
  const w = words(registerOf(me));

  // One `now` for the whole page, so a date and its countdown cannot straddle
  // midnight and disagree with each other — the same rule the project head follows.
  const today = new Date();
  const entries = projects ?? [];
  const rows = ranked(entries, today);
  const dates = timeline(entries, today);
  const { waiting, parked } = unscheduled(entries, today);

  /**
   * The one thing this project is asking for today, or nothing.
   *
   * One, not all of them: this is a row on a phone, and the project's own page
   * is where the rest of its calendar lives. Which one is already decided —
   * `pressingDates` sorts soonest first, and a date outranks an undated
   * commitment for the same reason it does in `rank`.
   */
  const marker = (entry: BoardEntry) => {
    const due = pressingDates(entry.project, today)[0];
    if (due) {
      const days = daysUntil(due.date, today);
      return (
        <span className={`row-when${days < 0 ? ' is-late' : ''}`}>
          {countdown(days)} · {due.what}
        </span>
      );
    }
    const nag = nagging(entry.project, today)[0];
    if (nag) return <span className="row-when is-undated">needs a date · {nag.what}</span>;
    return null;
  };

  return (
    /* The phone header floats over this, so this is what decides whether it is
       an object or nothing at all — see `topbarFloating` in console.tsx. */
    <div className="stream board" onScroll={onStreamScroll}>
      <header className="board-head">
        <h1>{w('boardTitle')}</h1>
        <p>{w('boardLede')}</p>
      </header>

      {projectsError && <div className="muted">{projectsError}</div>}
      {!projectsError && !projects && <BoardSkeleton />}

      {projects && (dates.length > 0 || waiting.length > 0 || parked.length > 0) && (
        <section className="board-when">
          <h2 className="section-label">On the calendar</h2>
          <ul className="line">
            {dates.map(({ entry, date }) => {
              const days = daysUntil(date.date, today);
              return (
                <li
                  key={`${entry.path}-${date.date}-${date.what}`}
                  className={`line-row${days < 0 ? ' is-late' : ''}${date.tentative ? ' is-tentative' : ''}`}
                >
                  <span className="line-date">{stamp(date.date)}</span>
                  <span className="line-count">{countdown(days)}</span>
                  <span className="line-what">{date.what}</span>
                  <button className="line-who" onClick={() => openPath(entry.path)}>
                    {entry.project.title}
                  </button>
                </li>
              );
            })}
            {/* Below the dated rows and not among them: a list ordered by date
                cannot hold something with no date without lying about when it
                is. These are chased every morning, which nothing above them is. */}
            {waiting.map(({ entry, item }) => (
              <li className="line-row is-undated" key={`${entry.path}-${item.what}`}>
                <span className="line-date">needs a date</span>
                <span className="line-count" />
                <span className="line-what">{item.what}</span>
                <button className="line-who" onClick={() => openPath(entry.path)}>
                  {entry.project.title}
                </button>
              </li>
            ))}
            {parked.map(({ entry, item }) => (
              <li className="line-row is-dormant" key={`${entry.path}-${item.what}`}>
                <span className="line-date">until {stamp(item.dormantUntil!)}</span>
                <span className="line-count" />
                <span className="line-what">{item.what}</span>
                <button className="line-who" onClick={() => openPath(entry.path)}>
                  {entry.project.title}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {rows.length > 0 && (
        <section className="board-tier">
          <ul className="rows">
            {rows.map(entry => (
              <li key={entry.path}>
                <button
                  className={`row${entry.project.milestone ? '' : ' is-unset'}`}
                  onClick={() => openPath(entry.path)}
                >
                  <span className="row-name">{entry.project.title}</span>
                  <span className="row-milestone">
                    {entry.project.milestone || w('noMilestone')}
                  </span>
                  {marker(entry)}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {projects && <StartProject empty={rows.length === 0} />}
    </div>
  );

  /* Declared inside so it can reach `w` and `createProject` without either
     being threaded through props — this is one component split for reading
     rather than for reuse. */
  function StartProject({ empty }: { empty: boolean }) {
    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState('');
    const [busy, setBusy] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);

    async function submit(e: React.FormEvent) {
      e.preventDefault();
      const name = title.trim();
      if (!name || busy) return;
      setBusy(true);
      setProblem(null);
      try {
        const made = await createProject(name);
        setTitle('');
        setOpen(false);
        openPath(made.path);
      } catch (err) {
        setProblem((err as Error).message);
      } finally {
        setBusy(false);
      }
    }

    return (
      <footer className={`board-foot${empty ? ' is-empty' : ''}`}>
        {empty && <p className="board-empty">{w('boardEmpty')}</p>}
        {!open ? (
          <button className="row" onClick={() => setOpen(true)}>
            <span className="row-name">{w('newProject')}</span>
            <span className="row-milestone">{w('newProjectNote')}</span>
          </button>
        ) : (
          <form className="make" onSubmit={submit}>
            <label className="make-label" htmlFor="new-project">
              {w('projectName')}
            </label>
            <input
              id="new-project"
              className="make-input"
              value={title}
              onChange={e => setTitle(e.target.value)}
              autoFocus
              autoComplete="off"
            />
            <div className="make-actions">
              <button className="make-go" type="submit" disabled={busy || !title.trim()}>
                {busy ? 'Starting…' : w('newProject')}
              </button>
              <button
                className="make-cancel"
                type="button"
                onClick={() => {
                  setOpen(false);
                  setProblem(null);
                }}
              >
                Cancel
              </button>
            </div>
            {problem && <p className="make-problem">{problem}</p>}
          </form>
        )}
      </footer>
    );
  }
}

/**
 * The board that is coming, in the shape it will arrive in.
 *
 * Fixed widths rather than anything derived from content, for the reason the
 * wiki tree's skeleton has them: a random or locale-dependent width is one thing
 * on the server and another in the browser, which React calls a hydration
 * mismatch.
 */
const ROW_SKELETON = ['62%', '84%', '48%'];

function BoardSkeleton() {
  return (
    <section className="board-tier" aria-hidden="true">
      <ul className="rows">
        {ROW_SKELETON.map((width, i) => (
          <li key={i}>
            <span className="row">
              <span className="skeleton skeleton-title" />
              <span className="skeleton" style={{ width }} />
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
